import { Navigate, useLocation, useSearchParams } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useEffectiveRoles } from '@/stores/impersonation'
import { useEmbedTheme } from '@/lib/useEmbedTheme'
import { AppShell } from './layout/AppShell'
import { ROUTES } from '@/constants/routes'
import {
  isFamilyLinkedRole,
  isOrgStaffRole,
  resolvePostLoginPath,
} from '@/lib/postLoginPath'
import { isAbsoluteHttpUrl } from '@/lib/lumenHost'

interface Props {
  children: React.ReactNode
  guestOnly?: boolean
  noShell?: boolean
  hideHeader?: boolean
  hideFab?: boolean
  requireOrgRole?: boolean
  requireFamilyRole?: boolean
}

/** Landing login that returns the user to the deep link after auth. */
function loginWithReturn(intendedPath: string): string {
  const path = intendedPath.startsWith('/') ? intendedPath : `/${intendedPath}`
  return `${ROUTES.LANDING}?modal=login&redirect=${encodeURIComponent(path)}`
}

/** Filament iframe shell — follows panel theme live via postMessage. */
function EmbedShell({ children }: { children: React.ReactNode }) {
  const theme = useEmbedTheme()
  const dark = theme === 'dark'
  return (
    <div
      data-embed-theme={theme}
      className={
        dark
          // Match Filament gray-950 panel body (zinc-ish neutrals, not blue-slate)
          ? 'min-h-[100dvh] bg-gray-950 text-gray-100 antialiased'
          : 'min-h-[100dvh] bg-white text-gray-900 antialiased'
      }
    >
      {children}
    </div>
  )
}


export function ProtectedRoute({
  children,
  guestOnly = false,
  noShell = false,
  hideHeader = false,
  hideFab = false,
  requireOrgRole = false,
  requireFamilyRole = false,
}: Props) {
  const user = useAuthStore((s) => s.user)
  const managedProfiles = useAuthStore((s) => s.userDoc?.managedProfiles)
  const roles = useEffectiveRoles()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const isLoggedIn = !!user
  const hasOrgRole = isOrgStaffRole(roles)
  const hasFamilyRole = isFamilyLinkedRole(roles, managedProfiles)
  const intended = `${location.pathname}${location.search}`

  // Filament org-panel iframe (?embed=1) or any framed load: no React sidebar/header/FAB.
  const isEmbed =
    searchParams.get('embed') === '1'
    || (typeof window !== 'undefined' && window.self !== window.top)

  // Already signed in on landing: always stay in this app (never auto-jump to Lumen).
  // Legacy ?redirect=/org… is remapped to /home — Lumen is opt-in only.
  const guestDest = guestOnly && isLoggedIn
    ? resolvePostLoginPath(roles, searchParams.get('redirect'))
    : null
  if (guestDest && isAbsoluteHttpUrl(guestDest)) {
    // Absolute Lumen URLs must not fire here — force family home.
    return <Navigate to={ROUTES.HOME} replace />
  }
  if (guestDest) {
    return <Navigate to={guestDest} replace />
  }

  // Deep links from org-panel (e.g. /org/sos) must not dump to bare "/" —
  // preserve path so post-login can return here.
  if (!guestOnly && !isLoggedIn) {
    if (requireOrgRole || location.pathname.startsWith('/org')) {
      return <Navigate to={loginWithReturn(intended)} replace />
    }
    return <Navigate to={ROUTES.LANDING} replace />
  }

  // Org staff arrive via invite / Firebase handoff — their Firebase account may
  // carry emailVerified=false (seeded/custom-token). Don't bounce org or embedded
  // loads to the verify page; the verify gate is for family self-signup only.
  const skipVerifyGate = isEmbed || requireOrgRole
  if (!guestOnly && isLoggedIn && user?.emailVerified === false && !skipVerifyGate) {
    return <Navigate to={ROUTES.VERIFY_EMAIL} replace />
  }

  // Logged in as family (or no org role) while opening org tools — prompt re-login
  // with return path (handoff from Filament may have been missing).
  if (requireOrgRole && !hasOrgRole) {
    return <Navigate to={loginWithReturn(intended)} replace />
  }

  // Org-only staff may open family-only routes (profiles/SOS/onboarding) — show
  // the shell anyway rather than banishing them to Lumen login. (SSO not solid.)
  if (requireFamilyRole && hasOrgRole && !hasFamilyRole) {
    // Fall through to AppShell so they can use /logout, home, settings.
  }

  if (guestOnly || noShell || isEmbed) {
    if (isEmbed) {
      return <EmbedShell>{children}</EmbedShell>
    }
    return <>{children}</>
  }
  return <AppShell hideHeader={hideHeader} hideFab={hideFab}>{children}</AppShell>
}
